///
/// EarthBrowser
///
/// PressureCenterNode - the H / L pressure centre marker
///
/// An extruded 3D letter lying flat in the tangent plane (north up, lit
/// like the other engine geometry), with the central pressure beneath it
/// and, when the centre is tracked across frames, its 3-hour tendency
/// (amber falling, blue rising). A low-contrast dark plate under the
/// symbol keeps it legible over cloud decks and snow, and a thin ring in
/// the centre's hue marks the footprint; both lie in the surface plane so
/// the marker foreshortens with the map rather than floating over it. The
/// group is scaled against the camera distance to hold a constant
/// on-screen size while close and shrink with the globe once far away.
/// Lows deepening quickly pulse a ring on the surface.
///
/// Hovering the marker shows the entity callout (EntityHoverHandler):
/// type, central pressure, 3 h tendency, motion from the track and the
/// forecast extreme.
///
/// Created by Matt Giger on 1/18/2026
/// Copyright (c) 2026 Geodata Labs
///

import { EntityHoverHandler } from "@/earthbrowser/datasets/EntityHoverHandler"
import { type InfoPanelData, type InfoPanelProvider } from "@/earthbrowser/datasets/InfoPanelProvider"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { TextGeometry } from "@/earthbrowser/entities/TextNode/TextGeometry"
import { TextRenderer } from "@/earthbrowser/entities/TextNode/TextRenderer"
import { type Font } from "@/earthbrowser/libs/opentype.js"
import { geographic } from "@/earthbrowser/utils/geographic"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { type Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { GestureNode } from "@stovokor/gestures/GestureNode"
import { type GestureHitResult } from "@stovokor/gestures/GestureRecognizer"
import { rgba, type color } from "@stovokor/math/color"
import { trig } from "@stovokor/math/math"
import { matrix } from "@stovokor/math/matrix"
import { sphere } from "@stovokor/math/sphere"
import { cross, normal, vec3 } from "@stovokor/math/vector"
import { SimpleMaterial } from "@stovokor/shader/Material"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { type PressureCenterInfo } from "@/redux/userInterfaceSlice"
import { BadgeGeometry } from "./BadgeGeometry"
import type { PressureCenterFeature } from "./SynopticTable"

// Centre hues (letter and ring)
const LOW_COLOR: color = rgba(1.0, 0.25, 0.22, 1.0)
const HIGH_COLOR: color = rgba(0.3, 0.58, 1.0, 1.0)
const LOW_VALUE_COLOR: color = rgba(1.0, 0.6, 0.55, 1.0)
const HIGH_VALUE_COLOR: color = rgba(0.6, 0.78, 1.0, 1.0)
const FALLING_COLOR: color = rgba(1.0, 0.74, 0.32, 1.0)
const RISING_COLOR: color = rgba(0.6, 0.86, 1.0, 1.0)
const PLATE_COLOR: color = rgba(0.02, 0.05, 0.12, 0.5)
const RING_ALPHA = 0.45

const ALTITUDE_OFFSET = 5.0  // km above surface to stay above terrain and isobars

// Layout in marker units (letter font size = 100). Scaled per frame so the
// letter's em is cTargetLetterPx on screen up to the distance cap
const cLetterSize = 100
const cLetterDepth = 0.28     // extrusion as a fraction of the font size
const cValueSize = 40
const cTendencySize = 28
const cLetterY = 18
const cValueY = -44
const cTendencyY = -80
const cPlateWidth = 150
const cPlateTop = 100
const cPlateBottomShort = -70
const cPlateBottomTall = -102
const cPlateRadius = 46
const cRingWidth = 3
const cTargetLetterPx = 32
const cMaxScaleDistanceKm = 8000
// Interpolated moves smaller than this on screen are not applied
const cMinVisibleMovePx = 0.25
// Tendencies this small are "steady" and not shown
const cMinTendencyHpa = 1
// Lows deepening at least this fast pulse
const cPulseTendencyHpa = -3
const cPulsePeriodMs = 2600
const cPulseMaxRadius = 2.4  // in plate half-widths
// Text and plate lighting: mostly ambient so the marker stays readable on
// the night side, with enough diffuse left for the extrusion to shade
const cAmbient = 0.78

class LitRenderer extends TextRenderer {
	constructor(context: StovokorContext, geometry: TextGeometry | BadgeGeometry, material: SimpleMaterial) {
		super(context, geometry, material, cAmbient)
	}
}

class PartNode extends NodeBase {
	constructor(renderer: Renderer) {
		super()
		this.renderer = renderer
	}

	/** Renderers bind their geometry once, so new geometry needs a new renderer */
	public setRenderer(renderer: Renderer): void {
		this.renderer = renderer
	}
}

/** One text string, re-tessellated and re-centred when its content changes */
class TextPart {
	public readonly node: PartNode
	public readonly material: SimpleMaterial
	private geometry: TextGeometry
	private renderer: LitRenderer
	public text: string

	public boundsSummary(): number[] {
		const b = this.geometry.bounds
		return [b.minx, b.maxx, b.miny, b.maxy, b.minz, b.maxz].map(v => Math.round(v * 10) / 10)
	}

	constructor(
		private context: StovokorContext,
		private font: Font,
		text: string,
		private size: number,
		private centerY: number,
		private depth: number,
		color: color
	) {
		this.text = text
		this.material = new SimpleMaterial(color)
		this.material.transparency = true
		this.geometry = new TextGeometry(context, text, font, size, depth)
		this.renderer = new LitRenderer(context, this.geometry, this.material)
		this.node = new PartNode(this.renderer)
		this.center()
	}

	public setText(text: string): void {
		if (text === this.text) return
		this.text = text
		this.geometry.destroy()
		this.geometry = new TextGeometry(this.context, text, this.font, this.size, this.depth)
		this.renderer = new LitRenderer(this.context, this.geometry, this.material)
		this.node.setRenderer(this.renderer)
		this.center()
	}

	/**
	 * Glyph paths are y-down with the baseline at 0 and extrude along +Z.
	 * Flip to y-up (north) so the extrusion rises out of the surface, and
	 * centre the bounds on the requested position.
	 */
	private center(): void {
		const b = this.geometry.bounds
		const cx = (b.minx + b.maxx) / 2
		const cy = (b.miny + b.maxy) / 2
		// a.multiply(b) applies a first, then b: place in glyph space (y-down,
		// so the badge-space centreY enters negated), flip to y-up /
		// z-out-of-surface, then lift so the extrusion (centred on z = 0 by
		// TextGeometry) sits on top of the plate
		this.node.transform = matrix.translate(-cx, -cy - this.centerY, 0)
			.multiply(matrix.axisRotation(vec3.unitX, trig.pi))
			.multiply(matrix.translate(0, 0, this.size * this.depth))
	}

	public destroy(): void {
		this.geometry.destroy()
	}
}

/** Motion and forecast extreme derived from the centre's track */
export interface PressureTrackSummary {
	motion?: { speedKmh: number; bearingDeg: number }
	extreme?: { pressureHpa: number; timeMs: number }
}

export class PressureCenterNode extends GestureNode implements InfoPanelProvider {
	public readonly id: string
	/** Hover-only: the callout previews, but centres don't take camera focus */
	public readonly focusable: boolean = false
	public readonly feature: PressureCenterFeature
	/** Last tendency passed to updateTendency (hPa / 3 h), NaN when unknown */
	public lastTendency = NaN
	private trackSummary: PressureTrackSummary = {}
	private currentLon: number
	private currentLat: number
	private currentPressure: number
	private hitSphere = new sphere(1)

	/** Tangent-plane frame: X east, Y north, Z up, scaled per frame */
	private readonly ground = new NodeBase()
	private readonly letter: TextPart
	private readonly value: TextPart
	private readonly tendency: TextPart
	private readonly plateMaterial = new SimpleMaterial(PLATE_COLOR)
	private readonly ringMaterial: SimpleMaterial
	private readonly pulseMaterial: SimpleMaterial
	private readonly pulseNode: PartNode
	private plateNode?: PartNode
	private ringNode?: PartNode
	private plateGeometry?: BadgeGeometry
	private ringGeometry?: BadgeGeometry
	private pulseGeometry: BadgeGeometry
	private tall = false
	private lastTendencyText = ""
	private worldPos = vec3.zero
	private up = vec3.unitY
	// km per screen pixel at the marker, from the last updateScreenSpace;
	// 0 until then so the first interpolation always applies
	private kmPerPx = 0
	private pulsing = false
	private lastScale = 0

	constructor(context: EarthBrowserContext, feature: PressureCenterFeature, font: Font) {
		super(context)
		this.feature = feature
		this.id = `pressure-center-${feature.track_id || feature.id}`
		this.currentLon = feature.lon
		this.currentLat = feature.lat
		this.currentPressure = feature.pressure_hpa
		const low = feature.center_type === 'L'
		const hue = low ? LOW_COLOR : HIGH_COLOR
		this.addRecognizer(new EntityHoverHandler(context))

		this.plateMaterial.transparency = true
		this.ringMaterial = new SimpleMaterial(rgba(hue.r, hue.g, hue.b, RING_ALPHA))
		this.ringMaterial.transparency = true

		this.addNode(this.ground)
		this.rebuildPlate(context)

		this.letter = new TextPart(context, font, feature.center_type, cLetterSize, cLetterY, cLetterDepth, hue)
		this.value = new TextPart(context, font, Math.round(feature.pressure_hpa).toString(), cValueSize, cValueY, 0.15, low ? LOW_VALUE_COLOR : HIGH_VALUE_COLOR)
		this.tendency = new TextPart(context, font, "+0 /3h", cTendencySize, cTendencyY, 0.15, RISING_COLOR)
		this.tendency.node.visible = false
		this.ground.addNode(this.letter.node)
		this.ground.addNode(this.value.node)
		this.ground.addNode(this.tendency.node)

		// Deepening-low pulse ring, expanding on the surface
		this.pulseMaterial = new SimpleMaterial(rgba(hue.r, hue.g, hue.b, 0))
		this.pulseMaterial.transparency = true
		this.pulseGeometry = new BadgeGeometry(context, { width: cPlateWidth, height: cPlateWidth, cornerRadius: cPlateWidth / 2, strokeWidth: 4 })
		this.pulseNode = new PartNode(new LitRenderer(context, this.pulseGeometry, this.pulseMaterial))
		this.pulseNode.visible = false
		this.ground.addNode(this.pulseNode)

		this.setPosition(feature.lon, feature.lat)
	}

	/** (Re)build the plate and its ring for the current height */
	private rebuildPlate(context: StovokorContext): void {
		if (this.plateNode) this.ground.removeNode(this.plateNode)
		if (this.ringNode) this.ground.removeNode(this.ringNode)
		this.plateGeometry?.destroy()
		this.ringGeometry?.destroy()

		const bottom = this.tall ? cPlateBottomTall : cPlateBottomShort
		const height = cPlateTop - bottom
		const centerY = (cPlateTop + bottom) / 2
		this.plateGeometry = new BadgeGeometry(context, { width: cPlateWidth, height, cornerRadius: cPlateRadius })
		this.ringGeometry = new BadgeGeometry(context, { width: cPlateWidth, height, cornerRadius: cPlateRadius, strokeWidth: cRingWidth })
		this.plateNode = new PartNode(new LitRenderer(context, this.plateGeometry, this.plateMaterial))
		this.ringNode = new PartNode(new LitRenderer(context, this.ringGeometry, this.ringMaterial))
		this.plateNode.transform = matrix.translate(0, centerY, 0)
		this.ringNode.transform = matrix.translate(0, centerY, 0.5)
		this.ground.addNode(this.plateNode)
		this.ground.addNode(this.ringNode)
	}

	private setPosition(lonDeg: number, latDeg: number): void {
		const geo = new geographic(lonDeg * trig.rad, latDeg * trig.rad, ALTITUDE_OFFSET)
		this.worldPos = geo.cartesian
		this.up = normal(this.worldPos)
		// The node's own transform stays at the centre for hit testing; the
		// ground frame carries the orientation and scale (updateScreenSpace)
		this.transform = matrix.translate(this.worldPos)
		this.lastScale = 0
	}

	public updateInterpolated(lonDeg: number, latDeg: number, pressureHpa: number): void {
		this.currentLon = lonDeg
		this.currentLat = latDeg
		this.currentPressure = pressureHpa
		// At live speed the interpolated position creeps by metres per frame;
		// moving the node (and rebuilding its frame) for less than a quarter
		// pixel is invisible and would keep the frame loop awake
		const geo = new geographic(lonDeg * trig.rad, latDeg * trig.rad, ALTITUDE_OFFSET)
		const moved = geo.cartesian.subtract(this.worldPos).length
		if (moved > cMinVisibleMovePx * this.kmPerPx) {
			this.setPosition(lonDeg, latDeg)
		}
		this.value.setText(Math.round(pressureHpa).toString())
	}

	/** Dev diagnostics for __ebDebug.getSynoptic() */
	public debugParts(): Record<string, unknown> {
		const part = (p: TextPart) => ({ text: p.text, visible: p.node.visible, bounds: p.boundsSummary() })
		return { letter: part(this.letter), value: part(this.value), tendency: part(this.tendency), scale: this.lastScale }
	}

	public setTrackSummary(summary: PressureTrackSummary): void {
		this.trackSummary = summary
	}

	/**
	 * Show the pressure change over the last three hours (hPa). NaN or a
	 * change below the steady threshold hides the line.
	 */
	public updateTendency(deltaHpa3h: number): void {
		this.lastTendency = deltaHpa3h
		const show = Number.isFinite(deltaHpa3h) && Math.abs(deltaHpa3h) >= cMinTendencyHpa
		this.tendency.node.visible = show
		if (show !== this.tall) {
			this.tall = show
			this.rebuildPlate(this.context)
		}
		this.pulsing = this.feature.center_type === 'L' && Number.isFinite(deltaHpa3h) && deltaHpa3h <= cPulseTendencyHpa
		if (!this.pulsing) this.pulseNode.visible = false
		if (!show) return

		const rounded = Math.round(deltaHpa3h)
		const text = `${rounded > 0 ? "+" : "-"}${Math.abs(rounded)} /3h`
		if (text !== this.lastTendencyText) {
			this.lastTendencyText = text
			this.tendency.setText(text)
			this.tendency.material.color = rounded < 0 ? FALLING_COLOR : RISING_COLOR
		}
	}

	// ========== Hover callout ==========

	public getInfoPanelData(): InfoPanelData | undefined {
		const low = this.feature.center_type === 'L'
		const t = this.lastTendency
		const parts: string[] = []
		if (Number.isFinite(t) && Math.abs(t) >= cMinTendencyHpa) {
			const verb = t < 0 ? (low ? "Deepening" : "Weakening") : (low ? "Filling" : "Building")
			parts.push(`${verb} ${Math.abs(Math.round(t))} hPa / 3 h`)
		} else {
			parts.push("Steady")
		}
		const motion = this.trackSummary.motion
		if (motion) {
			parts.push(motion.speedKmh < 8 ? "nearly stationary" : `moving ${compassPoint(motion.bearingDeg)} ${Math.round(motion.speedKmh)} km/h`)
		}
		const center: PressureCenterInfo = {
			centerType: low ? 'L' : 'H',
			lat: this.currentLat,
			lon: this.currentLon,
			pressureHpa: this.currentPressure,
			tendency3h: t,
			motion,
			extreme: this.trackSummary.extreme,
		}
		return {
			title: `${low ? "Low" : "High"} ${Math.round(this.currentPressure)} hPa`,
			subtitle: parts.join(" · "),
			theme: "synoptic",
			contentType: "pressure_center",
			contentData: { type: "pressure_center", center },
		}
	}

	// ========== Hit testing ==========

	override gestureIntersection(state: RenderState): GestureHitResult | undefined {
		// Sphere sized to the plate footprint in world units (updateScreenSpace)
		const dist = this.hitSphere.intersect(state.ray)
		if (dist > 0) {
			return { node: this, hitPoint: state.ray.pointAt(dist), distance: dist }
		}
		return undefined
	}

	/**
	 * Size the marker for the camera distance and keep the tangent frame
	 * current. Called every frame while the layer is visible.
	 * @param cameraPos camera position in world space (km)
	 * @param worldPerPixelAtUnitDist world units per screen pixel at distance 1
	 */
	public updateScreenSpace(cameraPos: vec3, worldPerPixelAtUnitDist: number): void {
		const distance = cameraPos.subtract(this.worldPos).length
		this.kmPerPx = distance * worldPerPixelAtUnitDist
		const scale = (cTargetLetterPx * Math.min(distance, cMaxScaleDistanceKm) * worldPerPixelAtUnitDist) / cLetterSize

		if (Math.abs(scale - this.lastScale) > this.lastScale * 0.002) {
			this.lastScale = scale
			const n = this.up
			let east = cross(vec3.unitY, n)
			if (east.length < 1e-6) east = vec3.unitX
			east = normal(east)
			const north = cross(n, east)
			this.ground.transform = frame(east, north, n, scale)

			const hitRadius = scale * cPlateWidth * 0.6
			this.hitSphere = new sphere(hitRadius)
		}

		if (this.pulsing) {
			const t = (performance.now() % cPulsePeriodMs) / cPulsePeriodMs
			const r = 1 + (cPulseMaxRadius - 1) * t
			this.pulseNode.visible = true
			this.pulseNode.transform = matrix.scale(r, r, 1)
			const hue = this.ringMaterial.color
			this.pulseMaterial.color = rgba(hue.r, hue.g, hue.b, 0.55 * (1 - t) * (1 - t))
		}
	}

	override destroy(): void {
		this.letter.destroy()
		this.value.destroy()
		this.tendency.destroy()
		this.plateGeometry?.destroy()
		this.ringGeometry?.destroy()
		this.pulseGeometry.destroy()
		super.destroy()
	}
}

function compassPoint(bearingDeg: number): string {
	const points = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"]
	return points[Math.round(((bearingDeg % 360) + 360) % 360 / 45) % 8]
}

/** Column-major frame matrix with uniform scale on the axes */
function frame(x: vec3, y: vec3, z: vec3, s: number): matrix {
	return new matrix([
		x.x * s, x.y * s, x.z * s, 0,
		y.x * s, y.y * s, y.z * s, 0,
		z.x * s, z.y * s, z.z * s, 0,
		0, 0, 0, 1,
	])
}
